import { useEffect, useState } from 'react'
import { Building2, Calendar, Tags, Layers, Fuel, Network, TrendingUp, FileText, FileSpreadsheet, CalendarRange, Car } from 'lucide-react'
import api from '../services/api'
import LookupSelect from '../components/LookupSelect'
import { useEmpresa } from '../contexts/EmpresaContext'
import { Linha } from './LancamentoDepositos'
import { SeletorMultiplo, CartaoTotal } from './RelatorioConsumoArla'

interface Periodo {
  inicio: string; rotulo: string
  valor: number; litros: number; km: number
  media: number | null; custoKm: number | null; veiculos: number
}
interface Opcao { cod: string; des: string }
type Medida = 'valor' | 'litros' | 'km' | 'media' | 'custoKm'

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']
const fmt = (v: number, casas: number) => v.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas })
const fmtN = (v: number | null) => (v == null ? '—' : fmt(v, 2))
const compacto = (v: number) => new Intl.NumberFormat('pt-BR', { notation: 'compact', maximumFractionDigits: 1 }).format(v)
// ERP arredonda nesta tela.
const trunc2 = (a: number, b: number) => (b > 0 ? Math.round((a / b) * 100) / 100 : null)

const MEDIDAS: { key: Medida; rotulo: string; formatar: (v: number) => string; eixo: (v: number) => string }[] = [
  { key: 'valor', rotulo: 'Valor (R$)', formatar: v => `R$ ${fmt(v, 2)}`, eixo: v => compacto(v) },
  { key: 'litros', rotulo: 'Litros', formatar: v => `${fmt(v, 0)} L`, eixo: v => compacto(v) },
  { key: 'km', rotulo: 'Km rodados', formatar: v => `${fmt(v, 0)} km`, eixo: v => compacto(v) },
  { key: 'media', rotulo: 'Média (km/l)', formatar: v => `${fmt(v, 2)} km/l`, eixo: v => fmt(v, 1) },
  { key: 'custoKm', rotulo: 'Custo/Km (R$)', formatar: v => `R$ ${fmt(v, 2)}/km`, eixo: v => fmt(v, 2) },
]

// Topo do eixo em número "redondo" (1, 2, 2.5, 5 × 10^n).
function escalaY(max: number) {
  if (max <= 0) return { topo: 1, passo: 0.25 }
  const bruto = max / 4
  const mag = Math.pow(10, Math.floor(Math.log10(bruto)))
  const passo = [1, 2, 2.5, 5, 10].map(m => m * mag).find(p => p >= bruto) || 10 * mag
  return { topo: Math.ceil(max / passo) * passo, passo }
}

// Colunas de uma série só (sem legenda), eixo único, rótulo só no maior valor, tooltip no hover.
function GraficoColunas({ periodos, medida }: { periodos: Periodo[]; medida: Medida }) {
  const [hover, setHover] = useState<number | null>(null)
  const def = MEDIDAS.find(m => m.key === medida)!
  const valores = periodos.map(p => Number(p[medida] ?? 0))
  const { topo, passo } = escalaY(Math.max(...valores, 0))
  const W = 800, H = 190, ml = 56, mr = 12, mt = 20, mb = 34
  const pw = W - ml - mr, ph = H - mt - mb
  const banda = pw / Math.max(periodos.length, 1)
  const barra = Math.min(24, banda * 0.6)
  const y = (v: number) => mt + ph - (v / topo) * ph
  const idxMax = valores.indexOf(Math.max(...valores))
  const ticks: number[] = []
  for (let t = 0; t <= topo + 1e-9; t += passo) ticks.push(t)
  const rotuloX = (p: Periodo) => p.rotulo.replace(/^(\w{3})\w*\//, '$1/')

  return (
    <div style={{ position: 'relative' }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }} role="img" aria-label={`Gráfico de ${def.rotulo} por período`}>
        {ticks.map(t => (
          <g key={t}>
            <line x1={ml} x2={W - mr} y1={y(t)} y2={y(t)} stroke="#e5e7eb" strokeWidth={1} />
            <text x={ml - 8} y={y(t) + 4} textAnchor="end" fontSize={11} fill="#64748b" style={{ fontVariantNumeric: 'tabular-nums' }}>{def.eixo(t)}</text>
          </g>
        ))}
        {periodos.map((p, i) => {
          const v = valores[i]
          const cx = ml + banda * i + banda / 2
          const topoBarra = y(v), base = y(0)
          const h = Math.max(base - topoBarra, v > 0 ? 1 : 0)
          const r = Math.min(4, h, barra / 2)
          // Ponta arredondada (4px) e base reta.
          const d = h > 0
            ? `M${cx - barra / 2},${base} V${topoBarra + r} Q${cx - barra / 2},${topoBarra} ${cx - barra / 2 + r},${topoBarra} H${cx + barra / 2 - r} Q${cx + barra / 2},${topoBarra} ${cx + barra / 2},${topoBarra + r} V${base} Z`
            : ''
          return (
            <g key={p.inicio}>
              {/* Maior valor da medida sempre em laranja. */}
              {d && <path d={d} fill={i === idxMax && v > 0 ? (hover === i ? '#c2410c' : '#ea580c') : (hover === i ? '#14547f' : '#1b3f7b')} />}
              {i === idxMax && v > 0 && (
                <text x={cx} y={topoBarra - 7} textAnchor="middle" fontSize={11} fontWeight={700} fill="#0f172a">{def.eixo(v)}</text>
              )}
              <text x={cx} y={H - mb + 15} textAnchor="middle" fontSize={periodos.length > 14 ? 9 : 11} fill="#475569">{rotuloX(p)}</text>
              {/* Área de hover maior que a barra */}
              <rect x={ml + banda * i} y={mt} width={banda} height={ph} fill="transparent"
                onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} />
            </g>
          )
        })}
        <line x1={ml} x2={W - mr} y1={y(0)} y2={y(0)} stroke="#cbd5e1" strokeWidth={1} />
      </svg>
      {hover != null && (
        <div style={{
          position: 'absolute', pointerEvents: 'none', top: 8,
          left: `${((ml + banda * hover + banda / 2) / W) * 100}%`, transform: 'translateX(-50%)',
          background: '#0f172a', color: '#fff', borderRadius: 8, padding: '0.4rem 0.6rem', fontSize: '0.78rem', whiteSpace: 'nowrap',
          boxShadow: '0 6px 16px -6px rgba(0,0,0,0.4)',
        }}>
          <div style={{ fontWeight: 800 }}>{def.formatar(valores[hover])}</div>
          <div style={{ color: '#cbd5e1' }}>{periodos[hover].rotulo} · {periodos[hover].veiculos} veículo(s)</div>
        </div>
      )}
    </div>
  )
}

// Estatística de consumo da frota por mês ou quinzena (GeneXus WCOMFRO → WCOBFRO/WCOBFRQ).
export default function RelatorioConsumoFrota() {
  const { empresas } = useEmpresa()
  const hoje = new Date()
  const [agrupamento, setAgrupamento] = useState('')
  const [modelosSel, setModelosSel] = useState<string[]>([])
  const [tipo, setTipo] = useState('')
  const [combustiveisSel, setCombustiveisSel] = useState<string[]>([]) // vazio = todos
  const [empresasSel, setEmpresasSel] = useState<string[]>([]) // vazio = todas
  const [veiculo, setVeiculo] = useState('')
  const [modelo, setModelo] = useState('')
  const [modelosVeiculo, setModelosVeiculo] = useState<Opcao[]>([])
  const [mesIni, setMesIni] = useState(1)
  const [anoIni, setAnoIni] = useState(hoje.getFullYear())
  const [mesFim, setMesFim] = useState(hoje.getMonth() + 1)
  const [anoFim, setAnoFim] = useState(hoje.getFullYear())
  const [intervalo, setIntervalo] = useState<'M' | 'Q'>('M')
  const [incluirBaixados, setIncluirBaixados] = useState(false)
  const [graficosPdf, setGraficosPdf] = useState(true)

  const [agrupamentos, setAgrupamentos] = useState<Opcao[]>([])
  const [modelos, setModelos] = useState<Opcao[]>([])
  const [tipos, setTipos] = useState<Opcao[]>([])
  const [combustiveis, setCombustiveis] = useState<Opcao[]>([])
  const [res, setRes] = useState<Periodo[] | null>(null)
  const [intervaloRes, setIntervaloRes] = useState<'M' | 'Q'>('M')
  const [medida, setMedida] = useState<Medida>('valor')
  const [carregando, setCarregando] = useState(false)
  const [gerando, setGerando] = useState<'' | 'pdf' | 'excel'>('')
  const [erro, setErro] = useState('')

  useEffect(() => {
    api.get('/combustivel/agrupamentos').then(r => setAgrupamentos(r.data)).catch(() => {})
    api.get('/combustivel/modelos-agrupamento').then(r => setModelos(r.data)).catch(() => {})
    api.get('/combustivel/tipos-veiculo').then(r => setTipos(r.data)).catch(() => {})
    api.get('/combustivel/combustiveis-veiculo').then(r => setCombustiveis(r.data)).catch(() => {})
    api.get('/combustivel/modelos-veiculo').then(r => setModelosVeiculo(r.data)).catch(() => {})
  }, [])

  const params = () => ({
    agrupamento, modelos: modelosSel.join(','), tipo, combustiveis: combustiveisSel.join(','), empresas: empresasSel.join(','), veiculo, modelo, intervalo, incluirBaixados: incluirBaixados ? '1' : '0',
    inicio: `${anoIni}-${String(mesIni).padStart(2, '0')}`,
    fim: `${anoFim}-${String(mesFim).padStart(2, '0')}`,
  })

  async function gerar() {
    setErro('')
    setCarregando(true)
    try {
      const r = await api.get('/combustivel/relatorios/consumo-frota', { params: params() })
      setRes(r.data)
      setIntervaloRes(intervalo)
    } catch (e: any) {
      setRes(null)
      setErro(e?.response?.data?.error || 'Erro ao gerar relatório.')
    } finally {
      setCarregando(false)
    }
  }

  async function exportar(formato: 'pdf' | 'excel') {
    setErro('')
    setGerando(formato)
    try {
      const r = await api.get(`/combustivel/relatorios/consumo-frota/${formato}`, { params: { ...params(), graficos: formato === 'pdf' && graficosPdf ? '1' : '0' }, responseType: 'blob' })
      const tipoArq = formato === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      const url = URL.createObjectURL(new Blob([r.data], { type: tipoArq }))
      if (formato === 'pdf') {
        window.open(url, '_blank')
      } else {
        const a = document.createElement('a')
        a.href = url
        a.download = 'consumo-mensal-frota.xlsx'
        a.click()
        URL.revokeObjectURL(url)
      }
    } catch {
      setErro(formato === 'pdf' ? 'Erro ao gerar PDF.' : 'Erro ao gerar Excel.')
    } finally {
      setGerando('')
    }
  }

  const seletorMes = (mes: number, setMes: (m: number) => void, ano: number, setAno: (a: number) => void) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <select value={mes} onChange={e => setMes(Number(e.target.value))} style={{ maxWidth: 140 }}>
        {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
      </select>
      <span style={{ color: '#57606f' }}>/</span>
      <input type="number" value={ano} min={2000} max={2100} onChange={e => setAno(Number(e.target.value))} style={{ width: 90 }} />
    </div>
  )

  const total = res && res.length ? (() => {
    const t = res.reduce((s, p) => ({ valor: s.valor + p.valor, litros: s.litros + p.litros, km: s.km + p.km }), { valor: 0, litros: 0, km: 0 })
    return { ...t, media: trunc2(t.km, t.litros), custoKm: trunc2(t.valor, t.km) }
  })() : null

  const th: React.CSSProperties = { padding: '0.5rem 0.6rem', fontSize: '0.68rem', fontWeight: 800, color: '#fff', background: '#1b3f7b', textAlign: 'right', whiteSpace: 'nowrap', textTransform: 'uppercase' }
  const td: React.CSSProperties = { padding: '0.4rem 0.6rem', fontSize: '0.82rem', borderBottom: '1px solid #eef2f7', textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
      <div className="modal-box" style={{ width: '100%', maxWidth: 680, maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
        <div className="modal-header" style={{ background: 'linear-gradient(135deg, #1a6ea8, #14547f)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 9, background: 'rgba(255,255,255,0.15)' }}>
              <TrendingUp size={18} color="#fff" />
            </span>
            <div>
              <h2>Consumo Mensal Frota</h2>
              <div style={{ fontSize: '0.7rem', color: '#dbeafe', marginTop: 2 }}>Valor, litros, km, média e custo por km da frota</div>
            </div>
          </div>
        </div>

        <div className="modal-corpo">
          <Linha label="Agrupamento" icon={Network}>
            <select value={agrupamento} onChange={e => setAgrupamento(e.target.value)} style={{ maxWidth: 280 }}>
              <option value="">Todos</option>
              {agrupamentos.map(a => <option key={a.cod} value={a.cod}>{a.des}</option>)}
            </select>
            {agrupamentos.length === 0 && <div style={{ fontSize: '0.72rem', marginTop: 4, color: '#94a3b8' }}>Nenhum agrupamento cadastrado.</div>}
          </Linha>

          <Linha label="Mod. Agrupamento" icon={Layers}>
            <SeletorMultiplo opcoes={modelos} selecionados={modelosSel} onChange={setModelosSel} />
          </Linha>

          <Linha label="Modelo" icon={Layers}>
            <select value={modelo} onChange={e => setModelo(e.target.value)} style={{ maxWidth: 280 }}>
              <option value="">Todos</option>
              {modelosVeiculo.map(m => <option key={m.cod} value={m.cod}>{m.des}</option>)}
            </select>
          </Linha>

          <Linha label="Veículo" icon={Car}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <div style={{ maxWidth: 140 }}>
                <LookupSelect endpoint="/erp/veiculos" codKey="veicod" descKey="veipla" tagKey="veicod"
                  codValue={veiculo} descValue={veiculo} onSelect={cod => setVeiculo(cod)} placeholder="Todos" />
              </div>
              {veiculo
                ? <button className="btn btn-secondary" style={{ padding: '0.25rem 0.6rem', fontSize: '0.72rem' }} onClick={() => setVeiculo('')}>Todos</button>
                : <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>em branco = todos</span>}
            </div>
          </Linha>

          <Linha label="Tipo" icon={Tags}>
            <select value={tipo} onChange={e => setTipo(e.target.value)} style={{ maxWidth: 280 }}>
              <option value="">Todos</option>
              {tipos.map(t => <option key={t.cod} value={t.cod}>{t.des}</option>)}
            </select>
          </Linha>

          <Linha label="Combustível" icon={Fuel}>
            <SeletorMultiplo opcoes={combustiveis} selecionados={combustiveisSel} onChange={setCombustiveisSel} placeholderBusca="Buscar combustível..." />
          </Linha>

          <Linha label="Estabelecimento" icon={Building2}>
            <SeletorMultiplo opcoes={empresas.map(e => ({ cod: String(e.empcod), des: `${e.empcod} — ${e.empfan?.trim() || e.empnom?.trim()}` }))}
              selecionados={empresasSel} onChange={setEmpresasSel} placeholderBusca="Buscar empresa..." />
          </Linha>

          <Linha label="Início" icon={Calendar}>{seletorMes(mesIni, setMesIni, anoIni, setAnoIni)}</Linha>
          <Linha label="Fim" icon={Calendar}>{seletorMes(mesFim, setMesFim, anoFim, setAnoFim)}</Linha>

          <Linha label="Intervalo" icon={CalendarRange}>
            <select value={intervalo} onChange={e => setIntervalo(e.target.value as 'M' | 'Q')} style={{ maxWidth: 160 }}>
              <option value="M">Mensal</option>
              <option value="Q">Quinzenal</option>
            </select>
          </Linha>

          <Linha label="Veículos baixados" icon={Car}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.82rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={incluirBaixados} onChange={e => setIncluirBaixados(e.target.checked)} />
              Incluir veículos baixados
            </label>
            <div style={{ fontSize: '0.72rem', marginTop: 4, color: '#94a3b8' }}>Desmarcado fica igual ao ERP: não conta veículo baixado hoje, mesmo que tenha rodado no período.</div>
          </Linha>

          {erro && (
            <div style={{ marginTop: '0.9rem', fontSize: '0.8rem', fontWeight: 600, padding: '0.55rem 0.9rem', borderRadius: 8, background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' }}>
              {erro}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-primary" onClick={gerar} disabled={carregando}>{carregando ? 'Gerando...' : 'Gerar'}</button>
          <button className="btn" style={{ background: '#dc2626', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
            onClick={() => exportar('pdf')} disabled={!!gerando}>
            <FileText size={15} /> {gerando === 'pdf' ? 'Gerando PDF...' : 'PDF'}
          </button>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.78rem', color: '#475569', cursor: 'pointer' }}>
            <input type="checkbox" checked={graficosPdf} onChange={e => setGraficosPdf(e.target.checked)} />
            Gráficos no PDF
          </label>
          <button className="btn" style={{ background: '#15803d', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
            onClick={() => exportar('excel')} disabled={!!gerando}>
            <FileSpreadsheet size={15} /> {gerando === 'excel' ? 'Gerando Excel...' : 'Excel'}
          </button>
        </div>
      </div>

      {res && (
        <div className="modal-box" style={{ width: '100%', maxWidth: 'none', maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
          <div className="modal-corpo" style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
            {res.length === 0 && <div style={{ textAlign: 'center', color: '#94a3b8', padding: '1.2rem' }}>Nenhum abastecimento no período.</div>}

            {res.length > 0 && (
              <>

                <div style={{ overflowX: 'auto', border: '1px solid #bfdbfe', borderRadius: 8 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ ...th, textAlign: 'left' }}>{intervaloRes === 'Q' ? 'Quinzena' : 'Mês/Ano'}</th>
                        <th style={th}>Valor</th><th style={th}>Litros</th><th style={th}>Kms</th>
                        <th style={th}>Média</th><th style={th}>Custo/Km</th><th style={th}>Veículos</th>
                      </tr>
                    </thead>
                    <tbody>
                      {res.map((p, i) => (
                        <tr key={p.inicio} style={{ background: i % 2 ? '#f5f9fd' : '#fff' }}>
                          <td style={{ ...td, textAlign: 'left', fontWeight: 600 }}>{p.rotulo}</td>
                          <td style={td}>{fmt(p.valor, 2)}</td>
                          <td style={td}>{fmt(p.litros, 0)}</td>
                          <td style={td}>{fmt(p.km, 0)}</td>
                          <td style={td}>{fmtN(p.media)}</td>
                          <td style={td}>{fmtN(p.custoKm)}</td>
                          <td style={td}>{p.veiculos}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {total && (
                  <div style={{ borderRadius: 12, padding: '1rem 1.1rem 1.1rem', background: 'linear-gradient(135deg, #0f2a52, #1b3f7b)' }}>
                    <div style={{ color: '#fff', fontSize: '1rem', fontWeight: 800, letterSpacing: '0.02em', marginBottom: '0.85rem' }}>TOTAL GERAL</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '0.7rem' }}>
                      <CartaoTotal titulo="Valor" cor="#1b3f7b" itens={[{ rotulo: 'Total gasto', valor: `R$ ${fmt(total.valor, 2)}` }]} />
                      <CartaoTotal titulo="Litros" cor="#0e7490" itens={[{ rotulo: 'Consumidos', valor: fmt(total.litros, 0) }]} />
                      <CartaoTotal titulo="Kms" cor="#475569" itens={[{ rotulo: 'Rodados', valor: fmt(total.km, 0) }]} />
                      <CartaoTotal titulo="Média" cor="#047857" itens={[{ rotulo: 'km/l', valor: fmtN(total.media) }]} />
                      <CartaoTotal titulo="Custo/Km" cor="#7c3aed" itens={[{ rotulo: 'R$ por km', valor: `R$ ${fmtN(total.custoKm)}` }]} />
                    </div>
                  </div>
                )}
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: '0.7rem 0.9rem', background: '#fff', maxWidth: 820 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.6rem', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.9rem', fontWeight: 800, color: '#0f172a' }}>
                      {MEDIDAS.find(m => m.key === medida)!.rotulo} por {intervaloRes === 'Q' ? 'quinzena' : 'mês'}
                    </span>
                    <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                      {MEDIDAS.map(m => (
                        <button key={m.key} type="button" onClick={() => setMedida(m.key)} style={{
                          padding: '0.3rem 0.65rem', borderRadius: 999, fontSize: '0.74rem', fontWeight: 700, cursor: 'pointer',
                          border: `1px solid ${medida === m.key ? '#1b3f7b' : '#d1d5db'}`,
                          background: medida === m.key ? '#1b3f7b' : '#fff', color: medida === m.key ? '#fff' : '#475569',
                        }}>{m.rotulo}</button>
                      ))}
                    </div>
                  </div>
                  <GraficoColunas periodos={res} medida={medida} />
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
